import {
  ApplyAction,
  DeleteAction,
  DeleteFilterModal,
  EmptyFilters,
  FilterCard,
  ShareAction,
  ShareFilterModal,
  canShareFilter,
  deleteFilter,
} from '@ecosystem/newron-client-lib';
import { feedbackV2, overlays } from '@ecosystem/newron-design-system';
import React, { useEffect, useState } from 'react';
import { useAnalyticsContext } from '../analytics/analytics.provider';
import { userClient } from '../client';
import { ContextManager } from '../context/manager';
import { Loading } from '../design/loading/Loading';
import { mapDefinitions } from '../export/utils/mapDefinitions';
import { Context, Entity, FilterDefinition, FilterType, SavedFilter } from '../types';
import { useSavedFiltersContext } from './savedFilters.provider';
import { getFilterEventDetails, getJoinedLabel } from './utils';

const FAILED_TO_LOAD_ALERT =
  'There has been an error loading saved filters. Please refresh your browser to try again. If this issue persists, please contact your GfK representative.';
const REGION_KEYWORD = 'region';

const { Alert } = feedbackV2;
const { Toast } = overlays;

type FilterCriteria = Entity & { name: string; isApplicable: boolean; sortIdx: number };
type SortableFilterDefinition = FilterDefinition & { sortIdx: number };

const isSavedFiltersInFilterOptions = (filter: SavedFilter) => {
  return Object.entries(filter.data.values).every(([key, entities]) => {
    const filterOptionValues = getFilterOptionValuesByKey(key);
    const filterOptionType = getFilterOptionTypeByKey(key);

    if (filterOptionValues.length === 0 && filterOptionType !== FilterType.dateRange) {
      return false;
    }
    const savedFilterValues = entities.map(entity => entity.value);
    if (filterOptionType === FilterType.numericRange || filterOptionType === FilterType.dateRange) {
      return true;
    }

    return savedFilterValues.every(value => filterOptionValues.includes(value));
  });
};

const mapToFilterCriteria = (
  filter: SavedFilter,
  definitionsMap: Record<string, SortableFilterDefinition>
): FilterCriteria[] => {
  if (!filter.data.values) {
    return [];
  }
  const lastIdx = Object.keys(definitionsMap).length;
  return Object.keys(filter.data.values)
    .map(name => {
      const definition = definitionsMap?.[name];
      if (definition) {
        return {
          label: definition.label,
          name: name,
          sortIdx: definition.sortIdx,
          isApplicable: isSavedFiltersInFilterOptions(filter),
          value: getJoinedLabel(filter.data.values[name]),
        };
      } else {
        const cachedName = filter.data.definitionLabels?.find(label => label.value === name);
        return {
          label: cachedName?.label || name,
          name: name,
          sortIdx: lastIdx,
          isApplicable: false,
          value: getJoinedLabel(filter.data.values[name]),
        };
      }
    })
    .sort((a, b) => a.sortIdx - b.sortIdx);
};

const getAllFeaturedFilters = (context: Context) => {
  const ungroupedFeatureFilters = context.filters.sections.flatMap(section => section.ungrouped.definitions || []);

  const groupedFeatureFilters = context.filters.sections.reduce((acc, section) => {
    if (section.grouped) {
      for (const group of section.grouped) {
        acc.push(...group.definitions);
      }
    }
    return acc;
  }, []);

  return [...groupedFeatureFilters, ...ungroupedFeatureFilters];
};

const getFilterOptionTypeByKey = (key: string): string => {
  const context = ContextManager.getInstance().get();
  if (!context?.filters?.sections?.length) {
    return null;
  }

  const combinedFeatureFilters = getAllFeaturedFilters(context);
  const matchingFilters = combinedFeatureFilters.find(def => def.name === key);
  return matchingFilters?.type;
};

const getFilterOptionValuesByKey = (key: string): string[] => {
  const context = ContextManager.getInstance().get();
  if (!context?.filters?.sections?.length) {
    return [];
  }

  const combinedFeatureFilters = getAllFeaturedFilters(context);

  const matchingFilters = combinedFeatureFilters.find(def => def.name === key);
  if (matchingFilters?.type === 'numericRange') {
    return [matchingFilters.min, matchingFilters.max].map(value => value.toString());
  }
  if (!matchingFilters?.options?.length) {
    return [];
  }

  return matchingFilters.options.map(option => option.value);
};

function SavedFilters() {
  const { savedFilters, loading, error, fetch, newFilterId, setNewFilterId } = useSavedFiltersContext();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<SavedFilter>();
  const [definitionsMap, setDefinitionsMap] = useState<Record<string, SortableFilterDefinition>>();
  const [showNoNewFilterToast, setShowNoNewFilterToast] = useState(false);
  const { trackPageEvent } = useAnalyticsContext();

  const contextSubscriber = (context: Context) => {
    if (!context?.filters?.sections?.length) {
      return;
    }

    const sections = context.filters.sections;
    const definitionsEntries = mapDefinitions(sections).map((def, idx) => [def.name, { ...def, sortIdx: idx }]);
    const definitionsMap = Object.fromEntries(definitionsEntries);
    setDefinitionsMap(definitionsMap);
  };

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    ContextManager.getInstance().subscribeToSettingsChanges(contextSubscriber);
    return () => ContextManager.getInstance().unsubscribeFromSettingsChanges(contextSubscriber);
  }, []);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    return () => {
      setNewFilterId(null);
    };
  }, []);

  useEffect(() => {
    if (newFilterId && !!savedFilters) {
      const hasNewFilter = savedFilters.some(filter => filter.id === newFilterId);
      if (!hasNewFilter) {
        setShowNoNewFilterToast(true);
        setNewFilterId(null);
      }
    }
  }, [newFilterId, savedFilters]);

  const handleDeleteSelectedFilter = async () => {
    await deleteFilter(selectedFilter);
    setDeleteModalOpen(false);
    setSelectedFilter(undefined);
    await fetch();
    trackPageEvent(...getFilterEventDetails('Delete Saved Filter'));
  };

  if (!definitionsMap) {
    return null;
  }

  const unavailableFilters =
    savedFilters?.length > 0 ? savedFilters.filter(filter => !isSavedFiltersInFilterOptions(filter)) : [];

  const availableFilters =
    savedFilters?.length > 0 ? savedFilters.filter(filter => isSavedFiltersInFilterOptions(filter)) : [];

  return (
    <>
      <Loading isActive={loading} withIndicator />
      {!!error && <Alert title={FAILED_TO_LOAD_ALERT} type="error" />}
      {!error && !loading && savedFilters?.length === 0 && <EmptyFilters />}
      {availableFilters.length > 0 && (
        <FilterCardWrapper
          filters={availableFilters}
          definitionsMap={definitionsMap}
          setDeleteModalOpen={setDeleteModalOpen}
          setShareModalOpen={setShareModalOpen}
          setSelectedFilter={setSelectedFilter}
          newFilterId={newFilterId}
          setNewFilterId={setNewFilterId}
        />
      )}
      {unavailableFilters.length > 0 && (
        <UnavailableSection
          filters={unavailableFilters}
          definitionsMap={definitionsMap}
          setDeleteModalOpen={setDeleteModalOpen}
          setShareModalOpen={setShareModalOpen}
          setSelectedFilter={setSelectedFilter}
          newFilterId={newFilterId}
          setNewFilterId={setNewFilterId}
        />
      )}
      <DeleteFilterModal
        isOpen={deleteModalOpen}
        itemToDelete={selectedFilter}
        getUsers={userClient.getSameAccessUsers}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedFilter(undefined);
        }}
        onDelete={handleDeleteSelectedFilter}
      />
      <ShareFilterModal
        isOpen={shareModalOpen}
        itemToShare={selectedFilter}
        onClose={() => {
          setShareModalOpen(false);
          setSelectedFilter(undefined);
        }}
        getUsers={userClient.getSameAccessUsers}
        onSuccess={() => {
          fetch();
        }}
      />
      {showNoNewFilterToast && (
        <Toast type="error" onComplete={() => setShowNoNewFilterToast(undefined)}>
          This filter is no longer available because it was either deleted or unshared
        </Toast>
      )}
    </>
  );
}

function UnavailableSection({
  filters,
  definitionsMap,
  setDeleteModalOpen,
  setShareModalOpen,
  setSelectedFilter,
  newFilterId,
  setNewFilterId,
}) {
  return (
    <>
      <hr className="mt-4" />
      <div className="py-4">
        <h4 className="font-normal">Unavailable</h4>
        <p className="text-xs text-gray-100" style={{ color: '#9499A3' }}>
          These saved filters can not be applied in the selected market
        </p>
      </div>
      <hr className="mb-4" />
      <FilterCardWrapper
        filters={filters}
        definitionsMap={definitionsMap}
        setDeleteModalOpen={setDeleteModalOpen}
        setShareModalOpen={setShareModalOpen}
        setSelectedFilter={setSelectedFilter}
        newFilterId={newFilterId}
        setNewFilterId={setNewFilterId}
      />
    </>
  );
}

function FilterCardWrapper({
  filters,
  definitionsMap,
  setDeleteModalOpen,
  setShareModalOpen,
  setSelectedFilter,
  newFilterId,
  setNewFilterId,
}) {
  const handleApply = (filter: SavedFilter) => {
    const clearAllDefnitions = Object.keys(definitionsMap).reduce((acc, name) => {
      acc[name] = [];
      return acc;
    }, {});
    ContextManager.getInstance().updateFilterValues({ ...clearAllDefnitions, ...filter.data.values });
  };

  const getTrackingTags = elementName => () => ({
    elementName,
    elementType: 'Button',
    elementCategory: 'Saved Filters',
  });

  return filters.map((filter, idx) => {
    const filterCriteria = mapToFilterCriteria(filter, definitionsMap);
    const canShare = canShareFilter(filter);
    const isNew = filter.id === newFilterId;

    if (isNew) {
      const element = document.querySelector(`[data-test-id='filter-${filter.id}']`);
      element && element.scrollIntoView({ behavior: 'smooth' });
    }

    // if have region filter, check if region is applicable by checking the current
    // available filters against the values, if not disable it
    filterCriteria.forEach(criteria => {
      const name = criteria.name;

      if (!name.includes(REGION_KEYWORD)) {
        return;
      }
      const regionOptionsValues = getFilterOptionValuesByKey(name);

      if (regionOptionsValues.length <= 0) {
        return;
      }
      const savedFilterValues = filter.data.values[name].map(entity => entity.value);
      for (const savedFilterValue of savedFilterValues) {
        if (!regionOptionsValues.includes(savedFilterValue)) {
          criteria.isApplicable = false;
        }
      }
    });

    return (
      <FilterCard key={idx} filter={filter} status={isNew ? 'new' : null}>
        <FilterCard.Content>
          {filterCriteria.map((entity, idx) => (
            <div key={idx} className={`${!entity.isApplicable && 'text-inactive'}`}>
              <span className="text-xs">{`${entity.label}: `}</span>
              <span className="font-bold text-xs">{entity.value}</span>
            </div>
          ))}
        </FilterCard.Content>
        <FilterCard.Footer>
          <FilterCard.Footer.Actions>
            <ApplyAction
              onClick={() => {
                handleApply(filter);
                isNew && setNewFilterId(null);
              }}
              disabled={filterCriteria.some(x => !x.isApplicable)}
              getTrackingTags={getTrackingTags('Apply Saved Filter')}
            />
            <DeleteAction
              onClick={() => {
                setSelectedFilter(filter);
                setDeleteModalOpen(true);
                isNew && setNewFilterId(null);
              }}
            />
            <ShareAction
              onClick={() => {
                setSelectedFilter(filter);
                setShareModalOpen(true);
              }}
              disabled={!canShare}
              getTrackingTags={getTrackingTags('Share Saved Filter')}
            />
          </FilterCard.Footer.Actions>
        </FilterCard.Footer>
      </FilterCard>
    );
  });
}

export { SavedFilters };
